import { useState, useEffect } from 'react'
import { ArrowUp, Square } from 'lucide-react'
import { useChannelDispatch } from '@/features/channel-router'
import type { ChannelMissReason } from '@/features/_shared/channel-types'
import { useStudioLocale } from '@/i18n'
import { useStudioStore } from '@/store/studio-store'
import type { ChannelDispatchPhase } from '@/store/ide-v2-slice'
import { AMBIGUOUS_TARGET_REASON, SHACL_FAIL_PREFIX, humanizeFailureReason } from './components/miss-reason-copy'
import { COOGO_IDE_CHROME_COPY, type CoogoIdeChromeCopy } from './coogo-ide-chrome-copy'

function getGenerationStatusText({
  isReady,
  useRealChannel2,
  phase,
  message,
  copy,
}: {
  isReady: boolean
  useRealChannel2: boolean
  phase: ChannelDispatchPhase
  message: string | null
  copy: CoogoIdeChromeCopy
}): string {
  if (!isReady) return copy.emptyState.gettingWorkspaceReady
  // On failure the status `message` carries a RAW reason (a ChannelMissReason
  // code, a synthetic shaclFail:<pageId>, or already-humanized prose). Route it
  // through the shared humanizer so the pill never leaks engineering jargon.
  // The phase guard is the safety: running/progress strings (e.g. "Checking
  // project ontology patterns.") are left untouched by the humanizer pass-through,
  // but only failed-phase messages reach it at all.
  if (phase === 'failed' && message) {
    if (Object.prototype.hasOwnProperty.call(copy.missReason, message)) {
      return copy.missReason[message as ChannelMissReason]
    }
    if (message.startsWith(SHACL_FAIL_PREFIX)) {
      return copy.emptyState.pageDidNotPassChecks
    }
    if (message === AMBIGUOUS_TARGET_REASON) {
      return copy.emptyState.selectExactPart
    }
    return humanizeFailureReason(message).title
  }
  if (message) return message
  if (phase === 'failed') return copy.emptyState.couldNotCreate
  if (phase === 'aborted') return copy.emptyState.stopped
  if (phase === 'completed') return copy.emptyState.readyToReview
  return useRealChannel2 ? copy.emptyState.describeAppToBuild : copy.emptyState.describeAppToDraft
}

/**
 * Empty state for cogo-studio Build mode.
 *
 * Layout: focused welcome hero + big input + example chips.
 *
 * On submit (Sub-plan A3.3 Task 10):
 * - useChannelDispatch().dispatch(intent) routes through Channel 1 -> Channel 2.
 * - Hook updates store.dispatchedOutput (swaps into generated canvas) and appends
 *   ChatMessages (drives ChatRail content).
 * - Input + submit button disabled while !isReady (cold kg init).
 *
 * Issue #15: dispatchResetSignal subscription resets local input to "" when
 * clearDispatch is triggered from the store.
 */
export function EmptyState() {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const [input, setInput] = useState('')
  const { isReady, isRunning, dispatch, abort } = useChannelDispatch()
  const dispatchResetSignal = useStudioStore((s) => s.dispatchResetSignal)
  const useRealChannel2 = useStudioStore((s) => s.useRealChannel2)
  const channelDispatchStatus = useStudioStore((s) => s.channelDispatchStatus)

  useEffect(() => {
    setInput("");
  }, [dispatchResetSignal]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (isRunning) {
      abort();
      return;
    }
    if (!trimmed || !isReady) return;
    dispatch(trimmed);
  };

  const statusText = getGenerationStatusText({
    isReady,
    useRealChannel2,
    phase: channelDispatchStatus.phase,
    message: channelDispatchStatus.message,
    copy,
  })
  const statusLabel = isRunning
    ? copy.emptyState.working
    : channelDispatchStatus.mode === 'real' || useRealChannel2
      ? copy.ready
      : copy.emptyState.draft
  const inputDisabled = !isReady || isRunning
  const canSubmit = isReady && !isRunning && input.trim().length > 0

  return (
    <div className="flex h-full items-center justify-center px-8">
      <div className="flex w-full max-w-[520px] flex-col gap-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground leading-tight mb-2">
            {copy.emptyState.heroLineOne}
            <br />
            <span className="text-foreground">{copy.emptyState.heroLineTwo}</span>
          </h1>
          <p className="text-sm text-muted-foreground">{copy.emptyState.description}</p>
        </div>

        {/* Big input */}
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 shadow-sm transition-colors focus-within:border-ring">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing)
                handleSubmit();
            }}
            placeholder={
              isRunning
                ? copy.emptyState.workingPlaceholder
                : isReady
                  ? copy.emptyState.inputPlaceholder
                  : copy.emptyState.preparing
            }
            disabled={inputDisabled}
            className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground disabled:opacity-60"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isReady || (!isRunning && !canSubmit)}
            aria-label={isRunning ? copy.cancelGeneration : copy.emptyState.send}
            className={`px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center ${
              isRunning
                ? "bg-muted text-foreground"
                : "border border-border bg-foreground text-background hover:bg-foreground/90"
            }`}
          >
            {isRunning ? (
              <Square size={16} aria-hidden />
            ) : (
              <ArrowUp size={16} aria-hidden />
            )}
          </button>
        </div>

        <div
          data-testid="generation-contract-status"
          className="flex w-fit items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-xs text-muted-foreground"
        >
          <span className="font-mono text-[10px] uppercase tracking-wide text-foreground">
            {statusLabel}
          </span>
          <span>{statusText}</span>
        </div>

        {/* Bottom badge */}
        <div className="mt-auto bg-muted rounded-lg px-3 py-3 text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">{copy.emptyState.keepTalking}</strong>{' '}
          {copy.emptyState.conversationCanChange}
        </div>
      </div>
    </div>
  );
}
